
import React, { useState } from 'react';
import { usePitchPlayground } from '@/hooks/usePitchPlayground';
import { Pitch } from '@/types/pitch';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PitchCard from '@/components/pitch/PitchCard';
import PitchDetailDialog from '@/components/pitch/PitchDetailDialog';
import NewPitchForm from '@/components/pitch/NewPitchForm';
import BadgesDisplay from '@/components/pitch/BadgesDisplay';
import CategoryFilter from '@/components/pitch/CategoryFilter';

const PitchPlayground: React.FC = () => {
  const { user } = useAuth();
  const {
    pitches,
    loading,
    createPitch,
    toggleUpvote,
    selectedPitch,
    setSelectedPitch,
    feedback,
    loadingFeedback,
    addFeedback,
    markFeedbackAsHelpful,
    userBadges,
    filterCategory,
    setFilterCategory
  } = usePitchPlayground();
  
  const [newPitchOpen, setNewPitchOpen] = useState(false);
  
  const handleCloseDetail = () => {
    setSelectedPitch(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Pitch Playground
          </h2>
          <p className="text-sm text-muted-foreground">Share your innovative ideas with the community</p>
        </div>
        
        {user && (
          <Button 
            onClick={() => setNewPitchOpen(true)}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white border-0 h-11 px-6 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Share Your Idea
          </Button>
        )}
      </div>
      
      <CategoryFilter 
        activeCategory={filterCategory} 
        onChange={setFilterCategory} 
      />
      
      {user && <BadgesDisplay badges={userBadges} />}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          // Loading state - show 6 skeleton cards
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="border border-border/50 rounded-2xl p-6 h-80 animate-pulse bg-gradient-to-br from-muted/50 to-muted/30">
              <div className="bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 h-6 w-3/4 rounded-lg mb-4"></div>
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 h-4 rounded-lg"></div>
                <div className="bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 h-4 w-5/6 rounded-lg"></div>
                <div className="bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 h-4 rounded-lg"></div>
              </div>
              <div className="bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 h-24 rounded-lg mb-4 mt-4"></div>
              <div className="flex justify-between items-center">
                <div className="bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 h-8 w-8 rounded-full"></div>
                <div className="bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 h-8 w-20 rounded-lg"></div>
              </div>
            </div>
          ))
        ) : pitches.length === 0 ? (
          <div className="col-span-full text-center py-16 bg-gradient-to-br from-muted/20 to-muted/10 rounded-2xl border border-border/50">
            <h3 className="text-2xl font-bold mb-3 text-foreground">
              No pitch ideas found
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
              Be the first to share your innovative idea with the community!
            </p>
          </div>
        ) : (
          pitches.map((pitch) => (
            <div key={pitch.id} className="transform hover:scale-105 transition-all duration-300">
              <PitchCard
                pitch={pitch}
                onSelect={() => setSelectedPitch(pitch)}
                onUpvote={() => toggleUpvote(pitch.id)}
              />
            </div>
          ))
        )}
      </div>
      
      {/* New Pitch Dialog */}
      <Dialog open={newPitchOpen} onOpenChange={setNewPitchOpen}>
        <DialogContent className="sm:max-w-[600px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl text-foreground">
              Share Your Idea
            </DialogTitle>
          </DialogHeader>
          <NewPitchForm 
            onSubmit={async (title, description, category) => {
              await createPitch(title, description, category);
              setNewPitchOpen(false);
            }}
            onCancel={() => setNewPitchOpen(false)}
          />
        </DialogContent>
      </Dialog>
      
      {/* Pitch Detail Dialog */}
      {selectedPitch && (
        <PitchDetailDialog 
          pitch={selectedPitch}
          feedback={feedback}
          isLoading={loadingFeedback}
          onClose={handleCloseDetail}
          onSubmitFeedback={addFeedback}
          onMarkHelpful={markFeedbackAsHelpful}
          onUpvote={toggleUpvote}
        />
      )}
    </div>
  );
};

export default PitchPlayground;
